import { formatCents } from "@caloteiros/shared";
import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import { Link, useParams } from "react-router-dom";
import logo from "../assets/logowtext.svg";
import { PixBox } from "../components/PixBox";
import { Avatar, EmptyState, LoadingScreen, Money } from "../components/ui";
import { firstName, formatShortDate } from "../lib/format";
import { usePublicGroup } from "../lib/queries";

/**
 * A tela que a galera do rolê abre. Sem login, sem conta, sem editar nada:
 * "quanto eu devo, para quem, e qual o Pix".
 */
export function PublicGroupPage(): ReactElement {
  const { shareToken = "" } = useParams();
  const group = usePublicGroup(shareToken);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);

  const peopleById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof group.data>["people"][number]>();
    for (const person of group.data?.people ?? []) map.set(person.id, person);
    return map;
  }, [group.data]);

  if (group.isLoading) return <LoadingScreen />;

  if (group.isError || !group.data) {
    return (
      <main className="auth-page">
        <EmptyState
          emoji="🔒"
          title="Link indisponível"
          description="Esse link foi desligado por quem criou o rolê."
          action={
            <Link className="btn btn-primary" to="/">
              Conhecer o Caloteiros
            </Link>
          }
        />
      </main>
    );
  }

  const data = group.data;
  const nameOf = (personId: string): string => peopleById.get(personId)?.displayName ?? "Alguém";
  const myTransfers = selectedPersonId
    ? data.transfers.filter((transfer) => transfer.fromPersonId === selectedPersonId)
    : [];
  const myReceivables = selectedPersonId
    ? data.transfers.filter((transfer) => transfer.toPersonId === selectedPersonId)
    : [];

  return (
    <div className="public-page">
      <header className="public-header">
        <img className="public-logo" src={logo} alt="Caloteiros" />
        <h1>
          {data.emoji ?? "🧾"} {data.name}
        </h1>
        <p style={{ opacity: 0.9 }}>
          {formatCents(data.totalCents)} em {data.expenseCount}{" "}
          {data.expenseCount === 1 ? "despesa" : "despesas"}
        </p>
      </header>

      <main className="app-content stack-lg" style={{ paddingBottom: "var(--space-7)" }}>
        {/* Primeiro passo: "quem é você?". Daí a tela vira pessoal. */}
        <section className="stack">
          <h2 className="section-title">Quem é você?</h2>
          <div className="chip-list">
            {data.people.map((person) => (
              <button
                key={person.id}
                type="button"
                className="chip"
                aria-pressed={selectedPersonId === person.id}
                onClick={() =>
                  setSelectedPersonId((current) => (current === person.id ? null : person.id))
                }
              >
                <Avatar name={person.displayName} color={person.avatarColor} size="sm" />
                {firstName(person.displayName)}
              </button>
            ))}
          </div>
        </section>

        {selectedPersonId ? (
          <section className="stack">
            <h2 className="section-title">O seu acerto</h2>
            {myTransfers.length === 0 && myReceivables.length === 0 ? (
              <div className="card">
                <EmptyState emoji="✅" title="Você está quitado" description="Nada a pagar por aqui." />
              </div>
            ) : (
              <div className="card">
                {myTransfers.map((transfer) => {
                  const creditor = peopleById.get(transfer.toPersonId);
                  return (
                    <div key={`pay-${transfer.toPersonId}`} className="transfer-card">
                      <div className="row-between">
                        <span>
                          Você paga <strong>{firstName(nameOf(transfer.toPersonId))}</strong>
                        </span>
                        <strong className="numeric negative">
                          {formatCents(transfer.amountCents)}
                        </strong>
                      </div>
                      <PixBox
                        pixKey={creditor?.pixKey ?? null}
                        pixKeyType={creditor?.pixKeyType ?? null}
                        ownerName={firstName(nameOf(transfer.toPersonId))}
                      />
                    </div>
                  );
                })}
                {myReceivables.map((transfer) => (
                  <div key={`receive-${transfer.fromPersonId}`} className="balance-row">
                    <span className="grow">
                      <strong>{firstName(nameOf(transfer.fromPersonId))}</strong> te deve
                    </span>
                    <strong className="numeric positive">{formatCents(transfer.amountCents)}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : null}

        <section className="stack">
          <h2 className="section-title">Todos os acertos</h2>
          <div className="card">
            {data.transfers.length === 0 ? (
              <EmptyState emoji="🎉" title="Tudo quitado" />
            ) : (
              data.transfers.map((transfer) => (
                <div
                  key={`${transfer.fromPersonId}-${transfer.toPersonId}`}
                  className="balance-row"
                >
                  <span className="grow">
                    {firstName(nameOf(transfer.fromPersonId))} → {firstName(nameOf(transfer.toPersonId))}
                  </span>
                  <span className="numeric">{formatCents(transfer.amountCents)}</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="stack">
          <h2 className="section-title">Saldo de cada um</h2>
          <div className="card">
            {data.people.map((person) => (
              <div key={person.id} className="balance-row">
                <Avatar name={person.displayName} color={person.avatarColor} />
                <span className="grow">{person.displayName}</span>
                <Money cents={person.balanceCents} signed />
              </div>
            ))}
          </div>
        </section>

        <section className="stack">
          <h2 className="section-title">Despesas</h2>
          <div className="card">
            {data.expenses.map((expense) => (
              <div key={expense.id} className="balance-row">
                <span className="grow">
                  <span className="list-item-title">{expense.description}</span>
                  <span className="faint" style={{ display: "block" }}>
                    {formatShortDate(expense.occurredAt)} ·{" "}
                    {expense.payerIds.map((id) => firstName(nameOf(id))).join(" + ")} pagou
                  </span>
                </span>
                <span className="numeric">{formatCents(expense.amountCents)}</span>
              </div>
            ))}
          </div>
        </section>

        <p className="center muted">
          Feito com <Link to="/">Caloteiros</Link>
        </p>
      </main>
    </div>
  );
}
